import { useEffect, useState } from "react";
import { cdName } from "../lib/filename";
import { http, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useRealtime } from "../context/RealtimeContext";
import { SECTIONS } from "../lib/sections";
import { Activity, Filter, LogIn, Plus, Pencil, Trash2, Download, ChevronLeft, ChevronRight, Archive, FileArchive } from "lucide-react";
import { DateInput } from "../components/DateInput";

const ICONS = { POST: Plus, PUT: Pencil, PATCH: Pencil, DELETE: Trash2, GET: Download };
const COLORS = { POST: "bg-emerald-500", PUT: "bg-sky-500", PATCH: "bg-sky-500", DELETE: "bg-rose-500", GET: "bg-violet-500" };
const fmtTime = (iso) => new Date(iso).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
const fmtDay = (iso) => new Date(iso).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const shift = (day, n) => { const d = new Date(`${day}T12:00:00`); d.setDate(d.getDate() + n); return ymd(d); };
const TODAY = () => ymd(new Date());
const MIN_DAY = () => shift(TODAY(), -60);

async function download(url, name) {
  const r = await http.get(url, { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data); a.download = cdName(r.headers["content-disposition"], name); a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

function Archives() {
  const [arch, setArch] = useState(null);
  useEffect(() => { http.get("/audit-archives").then((r) => setArch(r.data)); }, []);
  if (!arch) return <p className="text-sm text-slate-500">Chargement…</p>;
  if (!arch.length) return <div data-testid="archives-empty" className="text-center text-slate-500 py-16 border border-dashed border-slate-300 rounded-md"><Archive className="h-6 w-6 mx-auto mb-2 text-slate-300" />Aucune archive pour l&apos;instant. Les activités de plus de 60 jours sont archivées chaque nuit en comptes rendus PDF journaliers.</div>;
  const months = {};
  arch.forEach((a) => (months[a.month] ||= []).push(a));
  return (
    <div className="space-y-6" data-testid="archives-list">
      {Object.entries(months).map(([m, rows]) => (
        <section key={m} className="border border-slate-200 bg-white rounded-md" data-testid={`archive-month-${m}`}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <h3 className="font-display font-bold capitalize">{new Date(`${m}-01T12:00:00`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" })} <span className="text-xs font-normal text-slate-500">· {rows.length} jour(s) · {rows.reduce((t, r) => t + r.count, 0)} action(s)</span></h3>
            <button data-testid={`archive-zip-${m}`} onClick={() => download(`/audit-archives/month/${m}.zip`, `Journal_activite_${m}.zip`)} className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50"><FileArchive className="h-3.5 w-3.5" />Télécharger le mois (ZIP)</button>
          </div>
          <ul className="divide-y divide-slate-100">
            {rows.map((a) => (
              <li key={a.id} className="flex items-center gap-3 px-4 py-2 text-sm" data-testid={`archive-row-${a.id}`}>
                <span className="capitalize flex-1">{new Date(`${a.day}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</span>
                <span className="text-xs text-slate-500">{a.count} action(s) · {a.users} utilisateur(s)</span>
                <button data-testid={`archive-download-${a.id}`} onClick={() => download(`/audit-archives/${a.id}.pdf`, `Journal_activite_${a.day}.pdf`)} className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:underline"><Download className="h-3.5 w-3.5" />PDF</button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

const DR_STATUS = { pending: ["En attente", "bg-amber-100 text-amber-800"], approved: ["Supprimé", "bg-rose-100 text-rose-700"], refused: ["Refusée", "bg-slate-200 text-slate-600"] };

function DeletionRequests() {
  const { user } = useAuth();
  const { lastEvent } = useRealtime();
  const [items, setItems] = useState([]);
  const load = () => http.get("/deletion-requests").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  useEffect(() => { if (lastEvent?.event === "deletion_request_changed") load(); }, [lastEvent]); // eslint-disable-line react-hooks/exhaustive-deps
  const decide = async (r, approve) => {
    if (approve && !window.confirm(`Supprimer définitivement : ${r.label} ?`)) return;
    try {
      await http.post(`/deletion-requests/${r.id}/decide`, { approve });
      toast.success(approve ? "Élément supprimé" : "Demande refusée"); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (!items.length) return <p className="text-sm text-slate-500" data-testid="dr-empty">Aucune demande de suppression.</p>;
  return (
    <div className="space-y-3" data-testid="dr-list">
      {items.map((r) => { const [l, c] = DR_STATUS[r.status]; return (
        <div key={r.id} className="border border-slate-200 bg-white rounded-md p-4" data-testid={`dr-${r.id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <span className={`text-[11px] font-bold px-2 py-1 rounded ${c}`} data-testid={`dr-status-${r.id}`}>{l}</span>
            <span className="font-semibold">{r.label}</span>
            <span className="ml-auto text-xs text-slate-400">par {r.requested_by?.name || r.requested_by?.email} · {new Date(r.created_at).toLocaleString("fr-FR")}</span>
          </div>
          <div className="text-sm bg-slate-50 rounded px-3 py-2 mt-2"><b>Motif :</b> {r.reason}</div>
          {r.status !== "pending" && <div className="text-xs text-slate-500 mt-2">Décision de {r.decided_by} le {new Date(r.decided_at).toLocaleString("fr-FR")}</div>}
          {user?.is_primary_admin && r.status === "pending" && (
            <div className="flex gap-2 mt-3 justify-end">
              <button data-testid={`dr-refuse-${r.id}`} onClick={() => decide(r, false)} className="px-3 py-2 text-sm rounded-md border border-slate-300 hover:bg-slate-50">Refuser</button>
              <button data-testid={`dr-approve-${r.id}`} onClick={() => decide(r, true)} className="inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold rounded-md bg-rose-600 text-white hover:bg-rose-700"><Trash2 className="h-4 w-4" />Valider la suppression</button>
            </div>
          )}
        </div>
      ); })}
    </div>
  );
}

export default function AuditLog() {
  const [tab, setTab] = useState("journal");
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [f, setF] = useState({ user_id: "", section: "", day: TODAY() });
  const { lastEvent } = useRealtime();

  const load = async () => {
    const params = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    const r = await http.get("/audit-logs", { params });
    setItems(r.data);
  };
  useEffect(() => { http.get("/auth/users").then((r) => setUsers(r.data)); }, []);
  useEffect(() => { load(); }, [f]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (lastEvent && f.day === TODAY()) load(); }, [lastEvent]); // eslint-disable-line react-hooks/exhaustive-deps

  const sel = "text-sm border border-slate-300 rounded-md px-3 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-sky-500";
  const days = [];
  items.forEach((it) => {
    const d = it.at.slice(0, 10);
    if (!days.length || days[days.length - 1].day !== d) days.push({ day: d, first: it.at, rows: [] });
    days[days.length - 1].rows.push(it);
  });

  return (
    <div className="p-8 max-w-[1800px] mx-auto" data-testid="audit-page">
      <div className="mb-6">
        <h1 className="font-display text-4xl font-extrabold tracking-tight">Journal d'activité</h1>
        <p className="text-sm text-slate-600">Activités du jour en direct · 60 derniers jours consultables · au-delà, archives PDF journalières — visible uniquement par l'administrateur.</p>
      </div>
      <div className="flex gap-1 mb-5 border-b border-slate-200">
        {[["journal", "Journal"], ["archives", "Archives"], ["deletions", "Demandes de suppression"]].map(([k, l]) => (
          <button key={k} data-testid={`audit-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab === k ? "border-sky-600 text-sky-700" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>
        ))}
      </div>
      {tab === "deletions" ? <DeletionRequests /> : tab === "archives" ? <Archives /> : <>
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="audit-filters">
        <Filter className="h-4 w-4 text-slate-400" />
        <select data-testid="audit-filter-user" className={sel} value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })}>
          <option value="">Tous les utilisateurs</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role === "admin" ? "admin" : "secondaire"})</option>)}
        </select>
        <select data-testid="audit-filter-section" className={sel} value={f.section} onChange={(e) => setF({ ...f, section: e.target.value })}>
          <option value="">Toutes les rubriques</option>
          {SECTIONS.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
          <option value="auth">Comptes utilisateurs</option>
        </select>
        <button data-testid="audit-day-prev" disabled={f.day <= MIN_DAY()} onClick={() => setF({ ...f, day: shift(f.day, -1) })} className={`${sel} disabled:opacity-40`}><ChevronLeft className="h-4 w-4" /></button>
        <DateInput data-testid="audit-filter-day" min={MIN_DAY()} max={TODAY()} className={sel} value={f.day} onChange={(e) => e.target.value && setF({ ...f, day: e.target.value })} />
        <button data-testid="audit-day-next" disabled={f.day >= TODAY()} onClick={() => setF({ ...f, day: shift(f.day, 1) })} className={`${sel} disabled:opacity-40`}><ChevronRight className="h-4 w-4" /></button>
        {f.day !== TODAY() && <button data-testid="audit-day-today" onClick={() => setF({ ...f, day: TODAY() })} className="text-xs font-semibold text-sky-700 hover:underline">Aujourd&apos;hui</button>}
        <span className="ml-auto text-xs text-slate-500">{items.length} action(s)</span>
      </div>

      {items.length === 0 && <div className="text-center text-slate-500 py-16 border border-dashed border-slate-300 rounded-md"><Activity className="h-6 w-6 mx-auto mb-2 text-slate-300" />Aucune action enregistrée.</div>}
      <div className="space-y-8">
        {days.map((d) => (
          <section key={d.day}>
            <div className="sticky top-0 z-10 bg-slate-50/90 backdrop-blur py-1.5 text-[11px] font-bold uppercase tracking-widest text-slate-500 capitalize">{fmtDay(d.first)}</div>
            <ol className="relative border-l-2 border-slate-200 ml-3 mt-2 space-y-3">
              {d.rows.map((it) => <AuditRow key={it.id} it={it} />)}
            </ol>
          </section>
        ))}
      </div>
      </>}
    </div>
  );
}

function AuditRow({ it }) {
  const login = it.action === "Connexion";
  const Icon = login ? LogIn : ICONS[it.method] || Activity;
  return (
    <li className="ml-5" data-testid={`audit-row-${it.id}`}>
      <span className={`absolute -left-[11px] grid place-items-center h-5 w-5 rounded-full ring-4 ring-white text-white ${login ? "bg-slate-700" : COLORS[it.method] || "bg-slate-400"}`}><Icon className="h-3 w-3" /></span>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 bg-white border border-slate-200 rounded-md px-3 py-2">
        <span className="font-mono text-xs text-slate-500">{fmtTime(it.at)}</span>
        <span className="font-semibold text-sm text-slate-900">{it.user_name}</span>
        <span className={`text-[9px] font-bold uppercase tracking-widest px-1.5 py-0.5 rounded ${it.role === "admin" ? "bg-indigo-100 text-indigo-800" : "bg-sky-100 text-sky-800"}`}>{it.role === "admin" ? "admin" : "secondaire"}</span>
        <span className="text-sm text-slate-700">{it.action}</span>
        <span className="ml-auto font-mono text-[10px] text-slate-400 truncate max-w-[320px]" title={it.path}>{it.method} {it.path.replace("/api", "")}</span>
      </div>
    </li>
  );
}
